import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Lock } from 'lucide-react'
import { ApiError } from '../../api/client'
import { useFamilySettings } from '../../api/family-settings'
import {
  m2cUnresolved, m2cQueryReady, isM2cPreDispatchRefusal, useM2cPermitSafety, newM2cRequestKey, useM2cFullHistory, useM2cPreview, useM2cRun, useM2cStop, useM2cViewer,
  type M2cActionResult, type M2cBidAction, type M2cPilotPermit, type M2cPreviewResponse,
} from '../../api/m2c'
import { IS_DEMO_MODE } from '../../lib/demoMode'
import Card from '../Card'
import SectionHead from '../SectionHead'
import DataTable, { type DataTableColumn } from '../DataTable'
import { Badge } from '../StatusBadge'
import { M2cActionHistory, READ_ONLY_HINT, berlinDate, euro, fieldClass, outcomeText, primaryButton, reductionPct, secondaryButton, stateLabel } from './M2cActionHistory'
import { M2cReconcilePanel } from './M2cReconcilePanel'
import { M2cConfirmation } from './M2cConfirmation'
import { m2cReasonText } from './m2cReasons'
import Pagination from '../Pagination'

/** Pilot-Scope laut Owner decisions 2026-10-04; der Server bleibt maßgeblich. */
const PILOT_SCOPE = { profileId: 10, marketplace: 'DE', adType: 'SP', familyKey: '01_HGR', asin: 'B0DGPWWNHF' } as const
const STORAGE_KEY = 'm2c.pilot.selection'

interface Selection { permitId: string; revision: number }

function loadSelection(): Selection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<Selection>
    return typeof value.permitId === 'string' && value.permitId.trim() && Number.isInteger(value.revision) && (value.revision as number) >= 0
      ? { permitId: value.permitId, revision: value.revision as number } : null
  } catch { return null }
}
function saveSelection(selection: Selection): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selection)) } catch { /* nur Komfort */ }
}

export function permitOutOfScope(permit: M2cPilotPermit): boolean {
  return permit.profileId !== PILOT_SCOPE.profileId || permit.marketplace !== PILOT_SCOPE.marketplace || permit.adType !== PILOT_SCOPE.adType ||
    permit.familyKey !== PILOT_SCOPE.familyKey || permit.asin !== PILOT_SCOPE.asin || permit.campaigns.length !== 1
}

/** Abschnitt in Familien-Economics: Permit-Auswahl, danach Steuerung, Klärung und Verlauf. */
export function M2cPilotSection({ profileId }: { profileId: number }) {
  const id = useId()
  const [selection, setSelection] = useState<Selection | null>(loadSelection)
  const [draftId, setDraftId] = useState(selection?.permitId ?? '')
  const [draftRevision, setDraftRevision] = useState(selection ? String(selection.revision) : '')
  const [formError, setFormError] = useState('')
  if (profileId !== PILOT_SCOPE.profileId) return null

  function choose(next: Selection) {
    setSelection(next); saveSelection(next)
    setDraftId(next.permitId); setDraftRevision(String(next.revision))
  }
  function submit(event: FormEvent) {
    event.preventDefault()
    const revision = Number(draftRevision)
    if (!draftId.trim() || draftRevision.trim() === '' || !Number.isInteger(revision) || revision < 0) {
      setFormError('Permit-ID und Revision (ganze Zahl ab 0) aus dem Permit-Datensatz eingeben.')
      return
    }
    setFormError('')
    choose({ permitId: draftId.trim(), revision })
  }
  return <Card className="rounded-lg p-4 space-y-6 text-sm" role="region" aria-label="M2c-Pilot">
    <SectionHead title="M2c-Pilot: begrenzte Gebotssenkung"
      subtitle="Ein Permit, eine Kampagne, nur Keyword-Senkungen. Die Oberfläche hat keine Befugnis; der Server prüft jede Aktion." />
    <form className="grid gap-4 sm:grid-cols-3 sm:items-end" onSubmit={submit} noValidate>
      <div className="space-y-1"><label className="block font-medium" htmlFor={`${id}-permit`}>Permit-ID</label>
        <input id={`${id}-permit`} className={fieldClass} value={draftId} onChange={e => setDraftId(e.target.value)} aria-describedby={`${id}-hint`} /></div>
      <div className="space-y-1"><label className="block font-medium" htmlFor={`${id}-revision`}>Erwartete Permit-Revision</label>
        <input id={`${id}-revision`} className={fieldClass} type="number" min="0" step="1" value={draftRevision} onChange={e => setDraftRevision(e.target.value)} /></div>
      <button className={secondaryButton} type="submit">Permit laden</button>
      <p id={`${id}-hint`} className="text-xs text-[var(--muted-foreground)] sm:col-span-3">Werte aus dem vom Eigentümer erteilten Permit. Ohne Permit wird nichts geladen.</p>
      {formError && <p className="text-xs text-[var(--destructive)] sm:col-span-3">{formError}</p>}
    </form>
    {selection && <>
      <M2cPilotPanel key={selection.permitId} permitId={selection.permitId} revision={selection.revision}
        onRevision={revision => choose({ permitId: selection.permitId, revision })} />
      <M2cReconcilePanel key={`reconcile-${selection.permitId}`} permitId={selection.permitId} revision={selection.revision} />
      <M2cActionHistory permitId={selection.permitId} />
    </>}
  </Card>
}

type RunMessage = { kind: 'alert' | 'status'; text: string; reload?: boolean }

/** Bekannte Ablehnung vor jedem Senden (T4-Vertrag): kein unklarer Zustand. */
function definiteRefusal(err: unknown): RunMessage | null {
  if (!isM2cPreDispatchRefusal(err, 'run')) return null
  switch (err.status) {
    case 400: return { kind: 'alert', text: `Anfrage abgelehnt (400): ${err.message}. Nichts gesendet.` }
    case 403: return { kind: 'alert', text: `Keine Berechtigung (403): ${err.message}. Nichts gesendet.` }
    case 404: return { kind: 'alert', text: `Nicht gefunden (404): ${err.message}. Nichts gesendet; Permit-ID prüfen.` }
    case 409: return { kind: 'alert', text: `Daten wurden zwischenzeitlich geändert (409): ${err.message}. Laut Server nichts gesendet; Vorschau neu laden.`, reload: true }
    case 423: return { kind: 'alert', text: 'Read-only-Modus (423): Änderungen an Amazon sind gesperrt.' }
    case 503: return { kind: 'alert', text: `Voraussetzungen nicht erfüllt (503): ${err.message}. Sync-Frische, Sperre oder Stopp später neu prüfen.` }
    default: return null
  }
}

function runResultMessage(result: M2cActionResult | undefined, pilotStatus: 'active' | 'stopped'): RunMessage {
  if (!result) return { kind: 'alert', text: 'Server-Antwort ohne Ergebnis — Verlauf prüfen, nicht erneut senden.' }
  const outcome = result.outcome
  const stopped = pilotStatus === 'stopped' ? ' Pilot gestoppt.' : ''
  if (outcome?.status === 'unknown') {
    return { kind: 'alert', text: `Ergebnis unklar: Pilot gestoppt. Keine automatische Wiederholung — Aktion ${result.action.id} im Abschnitt „Klärung“ prüfen. Grund: ${outcome.reason}` }
  }
  if (outcome?.status === 'rejected') return { kind: 'alert', text: `Von Amazon abgelehnt: Pilot gestoppt. Keine Wiederholung. Grund: ${outcome.reason}` }
  if (outcome?.status === 'rejected_pre_dispatch') return { kind: 'status', text: `Vor dem Senden abgebrochen — keine Änderung an Amazon. Neue Vorschau prüfen. Grund: ${outcome.reason}${stopped}` }
  if (result.state === 'readback_verified' && result.readEvidence?.status === 'found') {
    return { kind: 'status', text: `Gesendet und per Readback bestätigt: ${euro(result.readEvidence.bidCents)}. Technische Bestätigung, kein Geschäftserfolg; Bewertung erst nach Beobachtung.${stopped}` }
  }
  if (outcome?.status === 'confirmed') return { kind: 'alert', text: `Amazon-Bestätigung erhalten, Readback nicht bestätigt (${stateLabel(result.state)}) — Klärung erforderlich, keine Wiederholung.${stopped}` }
  return { kind: 'alert', text: `Server-Ergebnis: ${stateLabel(result.state)} (${outcomeText(outcome)}).${stopped}` }
}

function runBlockers(input: {
  data: M2cPreviewResponse | null; permitMismatch: boolean; viewer: ReturnType<typeof useM2cViewer>; settings: ReturnType<typeof useFamilySettings>
  history: ReturnType<typeof useM2cFullHistory>; unknownLatch: boolean; refreshing: boolean; selected: M2cBidAction | null; running: boolean
}): Array<{ text: string; lock?: boolean }> {
  const { data, viewer, settings, history } = input
  const out: Array<{ text: string; lock?: boolean }> = []
  if (IS_DEMO_MODE) out.push({ text: READ_ONLY_HINT, lock: true })
  if (input.permitMismatch) out.push({ text: 'Antwort passt nicht zum angefragten Permit oder zur Revision.' })
  if (!data) return out
  const permit = data.permit
  if (permitOutOfScope(permit)) out.push({ text: 'Permit gehört nicht zu 01_HGR / B0DGPWWNHF / Profil 10 (DE, SP).' })
  if (viewer.isPending) out.push({ text: 'Benutzer wird geprüft.' })
  else if (viewer.error || !viewer.data) out.push({ text: 'Benutzer unbekannt — Ausführung gesperrt.' })
  else if (viewer.data.id !== permit.operatorId) {
    out.push({ text: `Ausführen nur durch den im Permit eingetragenen Operator (Benutzer-ID ${permit.operatorId}); admin oder * genügen nicht.` })
  }
  if (permit.status === 'stopped') out.push({ text: 'Pilot gestoppt — keine neuen Aktionen.' })
  const now = Date.now()
  if (now < Date.parse(permit.startsAt)) out.push({ text: `Permit beginnt am ${berlinDate(permit.startsAt)}.` })
  if (now >= Date.parse(permit.expiresAt)) out.push({ text: `Permit abgelaufen am ${berlinDate(permit.expiresAt)}.` })
  if (data.blockers.length > 0) out.push({ text: 'Server meldet Blocker.' })
  if (!m2cQueryReady(history) || !history.data?.complete) {
    out.push({ text: 'Verlauf nicht vollständig geladen — ungeklärte Aktionen nicht ausgeschlossen.' })
  } else {
    const unresolved = history.data.actions.filter(m2cUnresolved)
    if (unresolved[0]) out.push({ text: `Ungeklärte Aktion ${unresolved[0].action.id} (${stateLabel(unresolved[0].state)}) blockiert neue Läufe. Insgesamt ${unresolved.length} ungeklärte ${unresolved.length === 1 ? 'Aktion' : 'Aktionen'}.` })
  }
  if (settings.isPending || settings.error || !settings.data) out.push({ text: 'Familieneinstellungen nicht geladen — Abgleich mit dem Permit nicht möglich.' })
  else if (settings.data.settings.version !== permit.settingsVersion) {
    out.push({ text: `Familieneinstellungen geändert (Version ${settings.data.settings.version}, Permit Version ${permit.settingsVersion}) — Permit passt nicht mehr.` })
  }
  if (input.unknownLatch) out.push({ text: 'Ergebnis einer Permit-Aktion unbekannt — nicht erneut senden; Verlauf und Klärung prüfen.' })
  if (input.refreshing) out.push({ text: 'Vorschau wird aktualisiert.' })
  if (input.running) out.push({ text: 'Lauf wird ausgeführt.' })
  if (!input.selected) out.push({ text: 'Keine ausführbare Keyword-Senkung ausgewählt.' })
  return out
}

export function M2cPilotPanel({ permitId, revision, onRevision }: { permitId: string; revision: number; onRevision: (revision: number) => void }) {
  const id = useId()
  const preview = useM2cPreview(permitId, revision)
  const history = useM2cFullHistory(permitId)
  const viewer = useM2cViewer()
  const settings = useFamilySettings(PILOT_SCOPE.profileId)
  const run = useM2cRun(permitId), stop = useM2cStop(permitId)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<{ actionId: string; requestKey: string } | null>(null)
  const [runMessage, setRunMessage] = useState<RunMessage | null>(null)
  const safety = useM2cPermitSafety(permitId)
  const [page, setPage] = useState(0)
  const [blockedPage, setBlockedPage] = useState(0)
  const [stopReason, setStopReason] = useState('')
  const [stopMessage, setStopMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const runLock = useRef(false), stopLock = useRef(false), alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])

  const raw = preview.data ?? null
  const permitMismatch = raw !== null && (raw.permit.id !== permitId || raw.permit.revision !== revision)
  const data = raw && !permitMismatch ? raw : null
  const visiblePage = Math.min(page, Math.max(0, Math.ceil((data?.actions.length ?? 0) / 25) - 1))
  const candidatePage = Math.min(blockedPage, Math.max(0, Math.ceil((data?.blockedCandidates.length ?? 0) / 25) - 1))
  const selected = data?.actions.find(a => a.id === selectedId && a.entity.kind === 'keyword') ?? null
  const blockers = runBlockers({ data, permitMismatch, viewer, settings, history, unknownLatch: safety.latched, refreshing: !m2cQueryReady(preview), selected, running: run.isPending || safety.busy })
  const canRun = data !== null && blockers.length === 0

  async function confirmRun() {
    if (!data || !selected || !confirming || runLock.current || !canRun) return
    runLock.current = true
    try {
      const response = await run.mutateAsync({
        permitId: data.permit.id, expectedRevision: data.permit.revision,
        actions: [{ actionId: selected.id, expectedRevision: selected.revision }], requestKey: confirming.requestKey,
      })
      // Veraltete Antwort (Permit gewechselt): Komponente ist neu gemountet, nichts zuordnen.
      if (!alive.current) return
      const message = runResultMessage(response.results[0], response.pilotStatus)
      setRunMessage(message)
    } catch (err) {
      if (!alive.current) return
      const refusal = definiteRefusal(err)
      if (refusal) setRunMessage(refusal)
      else {
        setRunMessage({ kind: 'alert', text: 'Ergebnis unbekannt — nicht erneut senden. Verlauf und Klärung prüfen; der Server entscheidet über den Stand.' })
      }
    } finally {
      runLock.current = false
      if (alive.current) { setConfirming(null); setSelectedId(null) }
    }
  }

  async function submitStop() {
    if (!stopReason.trim() || stopLock.current) return
    stopLock.current = true
    try {
      const response = await stop.mutateAsync({ permitId, expectedRevision: revision, reason: stopReason.trim() })
      if (!alive.current || response.permitId !== permitId) return
      setStopMessage({ ok: true, text: `Pilot gestoppt (Revision ${response.revision}). Bereits gesendete Änderungen wurden nicht zurückgenommen; der Stopp ist kein Rollback.` })
      setStopReason('')
      onRevision(response.revision)
    } catch (err) {
      if (!alive.current) return
      if (isM2cPreDispatchRefusal(err, 'stop')) {
        setStopMessage({ ok: false, text: `Stopp vom Server abgelehnt (${err.status}): ${err.message}.${err.status === 403 ? ' Stoppen darf nur der im Permit eingetragene Operator mit M2c-Recht.' : ''} Pilot-Status unverändert.` })
      } else setStopMessage({ ok: false, text: err instanceof Error ? `Stopp nicht bestätigt: ${err.message}. Server-Status im Verlauf prüfen.` : 'Stopp nicht bestätigt.' })
    } finally { stopLock.current = false }
  }

  const columns: DataTableColumn<M2cBidAction>[] = [
    { key: 'select', label: 'Auswahl', render: a => a.entity.kind === 'keyword'
      ? <input type="radio" name={`${id}-action`} className="size-4 focus-visible:ring-2 focus-visible:ring-[var(--ring)]" aria-label={`Aktion ${a.id} auswählen`} checked={selectedId === a.id} onChange={() => setSelectedId(a.id)} />
      : <Badge tone="neutral">Target — nur Lesen</Badge> },
    { key: 'id', label: 'Aktion', render: a => <span className="font-mono text-xs">{a.id}</span> },
    { key: 'entity', label: 'Entity', render: a => <span>{a.entity.kind === 'keyword' ? 'Keyword' : 'Target'} <span className="font-mono text-xs">{a.entity.entityAmazonId}</span></span> },
    { key: 'scope', label: 'Kampagne / Gruppe', render: a => <span className="font-mono text-xs">{`${a.entity.campaignAmazonId} / ${a.entity.adGroupAmazonId}`}</span> },
    { key: 'bid', label: 'Gebot alt → Ziel', align: 'right', render: a => `${euro(a.oldBidCents)} → ${euro(a.targetBidCents)}` },
    { key: 'pct', label: 'Senkung', align: 'right', render: a => reductionPct(a.oldBidCents, a.targetBidCents) },
    { key: 'basis', label: 'Grundlage', render: a => <span title={a.businessKey}>{`Evidenz ${a.evidenceRevision} · Einstellungen v${a.settingsVersion}`}</span> },
  ]

  return <Card className="rounded-lg p-4 space-y-4" role="region" aria-label="M2c-Pilotsteuerung">
    <SectionHead level={3} title="Permit und Ausführung" subtitle="Vorschau ist reine Berechnung des Servers. Ausführen sendet höchstens eine Senkung; die Gebote berechnet der Server neu." />
    <button className={secondaryButton} disabled={preview.isFetching || history.isFetching} onClick={() => { void preview.refetch(); void history.refetch() }}>Vorschau und Verlauf aktualisieren</button>
    {preview.isPending && <div role="status" aria-label="Vorschau wird geladen" className="h-24 animate-pulse rounded-lg bg-[var(--muted)]" />}
    {preview.error && <div role="alert" className="space-y-2 text-[var(--destructive)]">
      <p>{preview.error instanceof ApiError && preview.error.status === 409
        ? 'Permit-Revision veraltet (409): aktuelle Revision im Permit-Datensatz prüfen und oben eingeben.'
        : preview.error.message}</p>
      <button className={secondaryButton} onClick={() => void preview.refetch()}>Erneut versuchen</button>
    </div>}
    {data && <>
      <div className="space-y-1">
        <p className="flex flex-wrap items-center gap-2"><span>{`Permit ${data.permit.id} · Revision ${data.permit.revision}`}</span>
          <Badge tone={data.permit.status === 'active' ? 'good' : 'bad'} size="md">{data.permit.status === 'active' ? 'Aktiv' : 'Gestoppt'}</Badge></p>
        <p>{`Familie ${data.permit.familyKey} · ASIN ${data.permit.asin} · ${data.permit.marketplace} · ${data.permit.adType} · Profil ${data.permit.profileId}`}</p>
        {data.permit.campaigns.map(c => <p key={c.campaignId}>{`Kampagne ID ${c.campaignId} (Amazon ${c.campaignAmazonId})`}</p>)}
        <p>{`Laufzeit ${berlinDate(data.permit.startsAt)} – ${berlinDate(data.permit.expiresAt)} (${data.limits.permitDays} Tage, keine Verlängerung)`}</p>
        <p>{`Operator: Benutzer-ID ${data.permit.operatorId}`}</p>
      </div>
      <details className="rounded-md border border-[var(--border)] p-3" open>
        <summary className="cursor-pointer font-medium">Grenzen und Fristen (laut Server, Version {data.limits.limitsVersion})</summary>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>{`Senkung je Gebot höchstens ${data.limits.maxReductionPercent} % und höchstens ${euro(data.limits.maxReductionCents)}; Mindestgebot ${euro(data.limits.minBidCents)}; Ausgangsgebot ${euro(data.limits.minBidCents)}–${euro(data.limits.maxSourceBidCents)}.`}</li>
          <li>{`Höchstens ${data.limits.maxDailyChanges} Änderungen pro Berlin-Tag im Pilot, ${data.limits.maxDailyCampaignChanges} pro Kampagne; Reservierungen, gesendete und unklare Aktionen zählen.`}</li>
          <li>{`Readback: höchstens ${data.limits.maxReadbacks} Leseversuche in ${data.limits.readbackWindowMs / 1000} Sekunden; ein unklares Ergebnis stoppt den gesamten Pilot.`}</li>
          <li>{`Beobachtung: ${data.limits.assessmentDays} volle Berlin-Tage ab dem Tag nach dem bestätigten Readback (T), davor ${data.limits.assessmentDays} Tage Vergleich; danach ${data.limits.maturityBufferDays} Tage Reife und Datenabdeckung; endgültige Bewertung frühestens am Tag T+${data.limits.assessmentDays + data.limits.maturityBufferDays}.`}</li>
          <li>{`Gleiche Entity frühestens nach ${data.limits.minRepeatDays} Tagen und nur nach abgeschlossener Bewertung (Erfolg oder Misserfolg) erneut senken; „Zu wenige Daten“ oder „Verfälscht“ geben nicht frei.`}</li>
          <li>{`Erfolg: Familienvolumen ≥${new Intl.NumberFormat('de-DE', { style: 'percent' }).format(data.limits.minVolumeRatio)}, Nettogewinn nicht schlechter und Ausgaben der festen Kohorte ≤${new Intl.NumberFormat('de-DE', { style: 'percent' }).format(data.limits.maxSpendRatio)} des Vergleichszeitraums.`}</li>
          <li>{`Early-Stop: Familienvolumen <${new Intl.NumberFormat('de-DE', { style: 'percent' }).format(data.limits.earlyStopVolumeRatio)} oder Nettogewinn-Differenz ≤${euro(data.limits.earlyStopProfitDeltaCents)}.`}</li>
          <li>Für die feste Kohorte gelten vergleichbare, reife Daten mit vollständiger Abdeckung. Early-Stop stoppt neue Aktionen; keine automatische Wiederherstellung. Tatsächliche Bewertungsfenster, Messwerte und Urteil sind noch nicht verfügbar.</li>
          <li>Targets: nur Lesen — Ausführung gesperrt.</li>
          <li>{`Wiederherstellung: je Aktion ${data.limits.recoveryDays} Tage ab erster Sendung, nur mit gesonderter Freigabe des Eigentümers.`}</li>
          <li>{`Sync-Alter: Entities höchstens ${data.limits.entitySyncMaxAgeHours} h, Metriken und Suchbegriffe höchstens ${data.limits.metricsSyncMaxAgeHours} h.`}</li>
        </ul>
      </details>
      {data.blockers.length > 0 && <div className="space-y-1"><p className="font-medium">Blocker laut Server</p>
        <ul className="list-disc pl-5">{data.blockers.map(b => <li key={b} title={b}>{m2cReasonText(b)}</li>)}</ul></div>}
      <DataTable columns={columns} rows={data.actions.slice(visiblePage * 25, (visiblePage + 1) * 25)} getRowId={a => a.id} emptyMessage="Keine Senkungen im Rahmen dieses Permits vorgeschlagen." />
      {data.blockedCandidates.length > 0 && <details className="rounded-md border border-[var(--border)] p-3">
        <summary className="cursor-pointer font-medium">{data.blockedCandidates.length === 1 ? '1 nicht ausführbarer Kandidat' : `${data.blockedCandidates.length} nicht ausführbare Kandidaten`}</summary>
        <div className="mt-2 space-y-2">
          <p className="text-xs text-[var(--muted-foreground)]">Diese Kandidaten werden nicht gesendet; sie sperren den Lauf der übrigen Aktionen nicht.</p>
          <ul aria-label="Nicht ausführbare Kandidaten" className="space-y-1">
            {data.blockedCandidates.slice(candidatePage * 25, (candidatePage + 1) * 25).map(c => <li key={`${c.entity.kind}:${c.entity.entityId}`} className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs">{`${c.entity.kind === 'keyword' ? 'Keyword' : 'Target'} ${c.entity.entityAmazonId} · ID ${c.entity.entityId}`}</span>
              <Badge tone="bad">Blockiert</Badge>
              {c.reasons.map(r => <span key={r} title={r} className="text-[var(--muted-foreground)]">{m2cReasonText(r)}</span>)}
            </li>)}
          </ul>
          {data.blockedCandidates.length > 25 && <Pagination label="Blockierte Kandidaten" page={candidatePage} pageSize={25} totalItems={data.blockedCandidates.length} pageSizeOptions={[25]} onPageChange={setBlockedPage} onPageSizeChange={() => {}} />}
        </div>
      </details>}
      {data.actions.length > 25 && <Pagination label="Vorschau" page={visiblePage} pageSize={25} totalItems={data.actions.length} pageSizeOptions={[25]} onPageChange={page => { setPage(page); setSelectedId(null) }} onPageSizeChange={() => {}} />}
      <div className="space-y-2">
        <ul aria-label="Ausführung gesperrt" className="space-y-1 text-xs text-[var(--muted-foreground)]">
          {blockers.map(b => <li key={b.text} className="flex items-center gap-1">{b.lock && <Lock className="size-4" aria-hidden="true" />}{b.text}</li>)}
        </ul>
        <button className={primaryButton} disabled={!canRun}
          onClick={() => selected && setConfirming({ actionId: selected.id, requestKey: newM2cRequestKey() })}>Senkung ausführen</button>
        {confirming && selected && <M2cConfirmation label="Senkung bestätigen" onClose={() => setConfirming(null)}>
          <p>{`1 Gebot wird gesenkt: ${euro(selected.oldBidCents)} → ${euro(selected.targetBidCents)} (Keyword ${selected.entity.entityAmazonId}). Fortfahren?`}</p>
          <p className="text-xs text-[var(--muted-foreground)]">Einmaliges Senden ohne automatische Wiederholung. Ein unklares Ergebnis stoppt den Pilot.</p>
          <div className="flex flex-wrap gap-2">
            <button className={primaryButton} disabled={run.isPending} onClick={() => void confirmRun()}>Bestätigen</button>
            <button data-m2c-cancel className={secondaryButton} disabled={run.isPending} onClick={() => setConfirming(null)}>Abbrechen</button>
          </div>
        </M2cConfirmation>}
      </div>
    </>}
    {runMessage && <div role={runMessage.kind} aria-label="Ergebnis des Laufs" className={runMessage.kind === 'alert' ? 'space-y-2 text-[var(--destructive)]' : 'space-y-2'}>
      <p>{runMessage.text}</p>
      {runMessage.reload && <button className={secondaryButton} onClick={() => { void preview.refetch(); void history.refetch() }}>Neu laden</button>}
    </div>}
    <div className="space-y-2 border-t border-[var(--border)] pt-4">
      <SectionHead level={4} title="Pilot stoppen" />
      <p>Stoppt nur neue Aktionen. Bereits gesendete Änderungen bleiben bei Amazon bestehen — der Stopp ist kein Rollback. Auch im Read-only-Modus möglich.</p>
      <label className="block font-medium" htmlFor={`${id}-stop`}>Begründung für den Stopp</label>
      <textarea id={`${id}-stop`} className={fieldClass} rows={2} value={stopReason} onChange={e => setStopReason(e.target.value)} />
      <button className={secondaryButton} disabled={!stopReason.trim() || stop.isPending} onClick={() => void submitStop()}>Pilot stoppen</button>
      {stopMessage && <p role="status" className={stopMessage.ok ? '' : 'text-[var(--destructive)]'}>{stopMessage.text}</p>}
    </div>
  </Card>
}

export default M2cPilotPanel
